import { futureValueSIP, monthlySWPForLumpsum } from "../../engine/calculations";

// Task 16.1 — one SIP, seen every way at once: final corpus, invested-vs-growth split, a
// year-by-year schedule, and the sustainable monthly income (SWP) that corpus could then pay.
// Composes existing engine functions per year — no new engine maths.
const config = {
  key: "sip-comprehensive",
  title: "SIP Comprehensive",
  category: "Comprehensive & Reports",
  description: "One SIP, seen every way: future value, invested vs growth, a year-by-year schedule, and the income it could then pay.",
  fields: [
    { key: "monthlySip", label: "Monthly SIP", type: "currency", default: 25_000, min: 500, max: 50_00_000 },
    { key: "returnRate", label: "Expected annual return", type: "percent", default: 12, min: 1, max: 30, assumptionKey: "equityReturn" },
    { key: "years", label: "Investment period (years)", type: "number", default: 20, min: 1, max: 40 },
    { key: "swpYears", label: "Then draw income for (years)", type: "number", default: 20, min: 1, max: 40, help: "How long the accumulated corpus should sustain a monthly withdrawal." },
    { key: "swpRate", label: "Return during withdrawal phase", type: "percent", default: 8, min: 0, max: 20, help: "Usually lower than the accumulation return — the corpus turns more conservative." },
  ],
  chart: {
    type: "line",
    xKey: "year",
    series: [
      { key: "invested", label: "Invested", colorVar: "--muted" },
      { key: "value", label: "Value", colorVar: "--gold" },
    ],
  },
  compute(values) {
    const monthlySip = Number(values.monthlySip) || 0;
    const annualRate = (Number(values.returnRate) || 0) / 100;
    const years = Number(values.years) || 0;
    const swpYears = Number(values.swpYears) || 0;
    const swpRate = (Number(values.swpRate) || 0) / 100;

    const fv = futureValueSIP(monthlySip, annualRate, years * 12);
    const invested = monthlySip * years * 12;
    const gains = fv - invested;
    const sustainableSwp = monthlySWPForLumpsum(fv, swpRate, swpYears);

    const series = [];
    const rows = [];
    for (let y = 1; y <= years; y++) {
      const yInvested = monthlySip * y * 12;
      const yValue = futureValueSIP(monthlySip, annualRate, y * 12);
      series.push({ year: y, invested: yInvested, value: yValue });
      rows.push({ year: y, invested: yInvested, value: yValue, gains: yValue - yInvested });
    }

    return {
      headline: { label: "Corpus after accumulation", value: fv },
      extraHeadlines: [
        { label: "Total invested", value: invested },
        { label: "Wealth gained", value: gains },
        { label: `Sustainable monthly income for ${swpYears}y`, value: sustainableSwp },
      ],
      breakdown: [
        { label: "Monthly SIP", value: monthlySip },
        { label: "Investment period", value: `${years} years`, format: "text" },
      ],
      series,
      table: {
        title: "Year-by-year",
        columns: [
          { key: "year", label: "Year", format: "text" },
          { key: "invested", label: "Invested" },
          { key: "value", label: "Value" },
          { key: "gains", label: "Gains" },
        ],
        rows,
      },
    };
  },
};

export default config;
